import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { avatarColor, initials } from '../lib/format';
import { IconX } from './icons';

/** Farbwelt eines Moduls oder Bausteins – steuert Verlauf, Kachel und Pille. */
export type Tone = 'primary' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

/* --------------------------- Modul-Kopfzeile -------------------------- */

/**
 * Kopfzeile eines Moduls: farbige Symbolkachel, Titel, optionale
 * Kennzeichnung, erklärender Untertitel und rechtsbündige Aktionen.
 *
 * Bewusst als eigene Komponente statt als Sammlung von Klassen: So bleibt
 * die Zuordnung Modul → Farbe → Symbol an einer Stelle nachvollziehbar und
 * jede Seite sieht ohne Zutun gleich aus.
 */
export function ModuleHeader({
  icon,
  title,
  subtitle,
  pill,
  tone = 'primary',
  actions,
}: {
  icon: ReactNode;
  title: string;
  subtitle?: ReactNode;
  pill?: string;
  tone?: Tone;
  actions?: ReactNode;
}) {
  return (
    <header className={`module-header tone-${tone}`}>
      <span className="module-icon">{icon}</span>
      <div className="module-text">
        <div className="module-title">
          <h1>{title}</h1>
          {pill && <span className="module-pill">{pill}</span>}
        </div>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="module-actions">{actions}</div>}
    </header>
  );
}

/* ---------------------------- Kennzahlkachel -------------------------- */

/**
 * Kennzahl mit Symbol, Wert und Einordnung. `delta` beschreibt die
 * Veränderung gegenüber einem Vergleichswert; die Richtung entscheidet über
 * die Farbe, nicht das Vorzeichen – bei „Überfällig“ ist ein Plus schlecht.
 */
export function StatCard({
  label,
  value,
  hint,
  icon,
  tone,
  delta,
  emphasis,
  action,
  onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  tone?: Tone | '';
  delta?: { text: string; direction: 'up' | 'down' | 'flat' };
  /**
   * Dringlichkeit. `alarm` ist dem Ernstfall vorbehalten und sollte auf einer
   * Seite höchstens einmal vorkommen – sonst nutzt sich die Wirkung ab.
   */
  emphasis?: 'attention' | 'alarm';
  /** Handlungsaufforderung, etwa „Sofort handeln“. */
  action?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={`kpi ${tone ? `accent-${tone}` : ''} ${emphasis ?? ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => event.key === 'Enter' && onClick() : undefined}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <div className="kpi-top">
        <span className="kpi-label" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          {emphasis === 'alarm' && <span className="kpi-pulse" aria-hidden="true" />}
          {label}
        </span>
        {icon && <span className="kpi-icon">{icon}</span>}
      </div>
      <span className="kpi-value">{value ?? 0}</span>
      {action && <span className="kpi-action">{action}</span>}
      {delta && <span className={`kpi-delta ${delta.direction}`}>{delta.text}</span>}
      {hint && <span className="kpi-hint">{hint}</span>}
    </div>
  );
}

/* ----------------------------- Fortschritt ---------------------------- */

export function Meter({
  value,
  max = 100,
  tone,
}: {
  value: number;
  max?: number;
  tone?: 'success' | 'warning' | 'danger' | 'accent';
}) {
  const percent = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <span className="meter">
      <span className={`meter-fill ${tone ?? ''}`} style={{ width: `${percent}%` }} />
    </span>
  );
}

/* ------------------------------ Badge -------------------------------- */

export function Badge({
  children,
  variant = '',
  color,
  dot,
}: {
  children: ReactNode;
  variant?: string;
  color?: string | null;
  dot?: boolean;
}) {
  if (color) {
    return (
      <span
        className="badge"
        style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}
      >
        {dot && <span className="dot" />}
        {children}
      </span>
    );
  }
  return (
    <span className={`badge ${variant}`}>
      {dot && <span className="dot" />}
      {children}
    </span>
  );
}

/* ------------------------------ Card --------------------------------- */

export function Card({
  title,
  subtitle,
  actions,
  children,
  bodyClassName = '',
  noBody,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  bodyClassName?: string;
  noBody?: boolean;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <div className="card-header">
          <div>
            {typeof title === 'string' ? <h2>{title}</h2> : title}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions && <div className="row">{actions}</div>}
        </div>
      )}
      {noBody ? children : <div className={`card-body ${bodyClassName}`}>{children}</div>}
    </section>
  );
}

/* ------------------------------ Modal -------------------------------- */

export function Modal({
  title,
  children,
  footer,
  onClose,
  size = '',
}: {
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  size?: '' | 'wide' | 'xwide';
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Im Dokumentkopf statt am Aufrufort: `backdrop-filter` an Seitenleiste und
  // Karten macht diese zum Bezugsrahmen für `position: fixed` – ein dort
  // geöffneter Dialog deckte nur die Leiste oder Karte ab, nicht das Fenster.
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className={`modal ${size}`} role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="btn ghost icon-only" onClick={onClose} aria-label="Schließen">
            <IconX />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------ Felder ------------------------------- */

export function Field({
  label,
  hint,
  error,
  children,
  required,
}: {
  label?: ReactNode;
  hint?: string;
  error?: string;
  children: ReactNode;
  required?: boolean;
}) {
  // Ein `<label>` ohne `htmlFor` ist für Screenreader und die
  // Ausfüllautomatik des Browsers unsichtbar mit seinem Feld verbunden – bei
  // über 300 Verwendungen dieser Komponente eine Lücke, die sich nicht an
  // jeder einzelnen Stelle schließen lässt. Deshalb hier: Trägt das einzelne
  // Kind bereits eine `id`, wird die übernommen; sonst erzeugt `useId()` eine
  // und wird hineingereicht. Umschließt `Field` mehr als ein Element – etwa
  // zwei zusammengehörige Eingaben für eine Spanne –, bleibt die Zuordnung
  // wie bisher aus, statt eine falsche Stelle zu erraten.
  const generatedId = useId();
  const onlyChild = Children.count(children) === 1 ? Children.only(children) : null;
  const childId = isValidElement(onlyChild) ? (onlyChild.props as { id?: string }).id : undefined;
  const fieldId = childId ?? generatedId;

  const content =
    isValidElement(onlyChild) && !childId
      ? cloneElement(onlyChild as any, { id: fieldId })
      : children;

  return (
    <div className="field">
      {label && (
        <label htmlFor={isValidElement(onlyChild) ? fieldId : undefined}>
          {label}
          {required && <span style={{ color: 'var(--danger)' }}> *</span>}
        </label>
      )}
      {content}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

/* --------------------------- Auswahl mit Suche ------------------------ */

export interface SelectOption {
  value: string;
  label: string;
  /** Zweite Zeile im Eintrag – etwa Ort, Kundennummer, Kennzeichen. */
  hint?: string;
  /** Zusätzlicher Suchtext, der nicht angezeigt wird (z. B. Artikelnummer). */
  keywords?: string;
  disabled?: boolean;
  /** Überschrift, unter der der Eintrag einsortiert wird. */
  group?: string;
}

/**
 * Vergleichsform für die Suche.
 *
 * Umlaute sind der eigentliche Grund für diese Funktion. Wer „Muller“ oder
 * „Mueller“ tippt, sucht „Müller“ – und wer eine Straße sucht, tippt sie
 * selten mit ß. Ohne diese Angleichung findet die Suche in einem deutschen
 * Datenbestand genau das nicht, wonach am häufigsten gesucht wird.
 */
function foldForSearch(value: string): string {
  return (
    value
      .toLowerCase()
      // Erst aufloesen ...
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      // ... dann zusammenziehen. Erst dieser zweite Schritt bringt alle drei
      // Schreibweisen auf dieselbe Form: „Mueller“, „Müller“ und „Muller“
      // werden gleichermaßen zu „muller“. Ohne ihn fände die Suche nur, wer
      // die Umlaute ohnehin schon richtig tippt – also gerade den Fall, für
      // den es diese Funktion nicht bräuchte.
      .replace(/ae/g, 'a')
      .replace(/oe/g, 'o')
      .replace(/ue/g, 'u')
      .replace(/ss/g, 's')
  );
}

/**
 * Prüft, ob ein Eintrag zur Eingabe passt.
 *
 * Die Eingabe wird in Wörter zerlegt, die alle vorkommen müssen – aber in
 * beliebiger Reihenfolge. „lohwieser haupt“ findet damit auch
 * „Hauptstraße 4 – Lohwieser“. Eine reine Teilzeichenkettensuche fände das
 * nicht, und genau so tippen Menschen.
 */
function optionMatches(option: SelectOption, words: string[]): boolean {
  if (!words.length) return true;
  const haystack = foldForSearch(`${option.label} ${option.hint ?? ''} ${option.keywords ?? ''}`);
  return words.every((word) => haystack.includes(word));
}

/**
 * Errechnet, wo die Klappliste von `SearchSelect` in Bildschirmkoordinaten
 * erscheinen soll.
 *
 * Öffnet sie nach unten nicht genug Platz, nach oben aber mehr, klappt sie
 * stattdessen nach oben auf – genau das verhindert, dass ein Feld nahe am
 * unteren Rand einer Karte oder eines Fensters seine eigene Liste
 * abschneidet.
 */
function computeSearchSelectPlacement(control: HTMLElement, panel: HTMLElement | null) {
  const rect = control.getBoundingClientRect();
  const gap = 4;
  const panelHeight = panel?.offsetHeight ?? 0;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openUp = panelHeight > spaceBelow - gap && spaceAbove > spaceBelow;
  return {
    top: openUp ? rect.top - panelHeight - gap : rect.bottom + gap,
    left: rect.left,
    width: rect.width,
  };
}

/**
 * Auswahlfeld mit Suche.
 *
 * Ersetzt `<select>` dort, wo die Liste lang genug ist, dass Scrollen
 * mühsam wird – Objekte, Mitarbeiter, Artikel, Dienstleister. Für ein
 * Statusfeld mit vier Einträgen ist es das falsche Werkzeug: Ein Suchfeld
 * über vier Einträgen macht die Bedienung langsamer, nicht schneller.
 * Deshalb erscheint die Suchzeile erst ab `searchFrom` Einträgen.
 *
 * Die Bedienung folgt dem, was ein `<select>` kann, damit niemand umlernen
 * muss: Pfeiltasten bewegen, Enter wählt, Escape bricht ab, Tippen sucht.
 */
export function SearchSelect({
  value,
  onChange,
  options,
  placeholder = 'Bitte wählen …',
  searchPlaceholder = 'Suchen …',
  emptyLabel = 'Kein Eintrag gefunden.',
  disabled,
  clearable = true,
  id,
  style,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  /** Blendet das Kreuz zum Leeren aus – für Pflichtfelder. */
  clearable?: boolean;
  id?: string;
  /** Wirkt auf das Feld selbst – die Klappliste richtet sich danach aus. */
  style?: CSSProperties;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [placement, setPlacement] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const controlRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const withSearch = options.length >= 8;

  const visible = useMemo(() => {
    const words = foldForSearch(query.trim()).split(/\s+/).filter(Boolean);
    return options.filter((o) => optionMatches(o, words));
  }, [options, query]);

  /** Beim Öffnen auf den gewählten Eintrag springen – nicht auf den ersten. */
  useEffect(() => {
    if (!open) return;
    setQuery('');
    const index = options.findIndex((o) => o.value === value);
    setActive(index >= 0 ? index : 0);
    if (withSearch) window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open, options, value, withSearch]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // Die Liste hängt per Portal unter <body>, nicht mehr unter diesem
      // Feld – ein Klick hinein wäre für `rootRef` allein ein „Klick
      // draußen“ und schlösse die Liste, noch bevor die Auswahl greift.
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  /** Der hervorgehobene Eintrag muss sichtbar bleiben, auch bei langen Listen. */
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector<HTMLElement>('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [open, active, visible.length]);

  /**
   * Wo die Liste erscheint – in Bildschirmkoordinaten, weil sie per Portal
   * unter <body> hängt (siehe Begründung bei `.search-select-panel` in
   * app.css). Läuft im selben Durchlauf wie das Öffnen: Die Liste steht zu
   * diesem Zeitpunkt schon im DOM (nur unsichtbar platziert), ihre Höhe lässt
   * sich also schon hier messen – eine zweite, sichtbare Neuausrichtung nach
   * dem ersten Anstreichen entfiele sonst nicht.
   */
  useLayoutEffect(() => {
    if (!open || !rootRef.current) {
      setPlacement(null);
      return;
    }
    setPlacement(computeSearchSelectPlacement(rootRef.current, panelRef.current));
  }, [open, visible.length, withSearch]);

  /** Bleibt beim Scrollen oder einer Größenänderung am Feld verankert. */
  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (rootRef.current) setPlacement(computeSearchSelectPlacement(rootRef.current, panelRef.current));
    };
    // Erfasst per Erfassungsphase auch das Scrollen eines Vorfahren wie
    // `.main` – ein Bildlaufereignis dort läuft nicht zum Fenster hoch.
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const choose = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  };

  /** Übersprungene Einträge: Ein gesperrter Eintrag darf die Tastatur nicht blockieren. */
  const step = (from: number, delta: number): number => {
    if (!visible.length) return 0;
    let index = from;
    for (let i = 0; i < visible.length; i += 1) {
      index = (index + delta + visible.length) % visible.length;
      if (!visible[index]?.disabled) return index;
    }
    return from;
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    if (!open) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive((i) => step(i, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive((i) => step(i, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActive(step(-1, 1));
        break;
      case 'End':
        event.preventDefault();
        setActive(step(0, -1));
        break;
      case 'Enter':
        event.preventDefault();
        if (visible[active]) choose(visible[active]);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        // Nur relevant, wenn das Suchfeld den Tastaturfokus trägt: Die
        // Liste hängt per Portal am Ende von <body> – tabbte der Browser
        // von dort aus weiter, läge das nächste erreichbare Feld irgendwo
        // hinter dem gesamten Seiteninhalt statt beim nächsten Feld im
        // Formular. Kein `preventDefault()`: Der Browser soll seinen
        // eigenen Tab-Schritt ganz normal ausführen, nur eben ausgehend
        // vom Knopf, der unverändert an seiner Stelle im Formular steht –
        // dorthin wird der Fokus davor synchron zurückgeholt.
        if (event.target !== controlRef.current) controlRef.current?.focus();
        break;
      default:
        break;
    }
  };

  // Gruppen erst hier bilden, damit die Filterung sie mitzieht: Eine
  // Überschrift ohne Einträge darunter wäre irreführend.
  let lastGroup: string | undefined;

  return (
    <div
      className={`search-select${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      ref={rootRef}
    >
      <button
        ref={controlRef}
        type="button"
        id={id}
        className="search-select-control"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={selected ? 'search-select-value' : 'search-select-value is-empty'}>
          {selected ? selected.label : placeholder}
        </span>
        {clearable && selected && !disabled && (
          <span
            className="search-select-clear"
            role="button"
            tabIndex={-1}
            aria-label="Auswahl aufheben"
            onClick={(event) => {
              event.stopPropagation();
              onChange('');
            }}
          >
            <IconX />
          </span>
        )}
        <span className="search-select-caret" aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="search-select-panel"
            style={{
              // Solange die Höhe noch nicht gemessen ist (der allererste
              // Durchlauf nach dem Öffnen), unsichtbar und außerhalb des
              // Bildschirms halten – sonst blitzte die Liste einen Moment an
              // der falschen Stelle auf, bevor `placement` feststeht.
              top: placement?.top ?? -9999,
              left: placement?.left ?? -9999,
              width: placement?.width,
              visibility: placement ? 'visible' : 'hidden',
            }}
          >
            {withSearch && (
              <input
                ref={inputRef}
                className="search-select-input"
                type="text"
                value={query}
                placeholder={searchPlaceholder}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
                onKeyDown={onKeyDown}
              />
            )}
            <div className="search-select-list" ref={listRef} role="listbox">
              {visible.length === 0 && <div className="search-select-empty">{emptyLabel}</div>}
              {visible.map((option, index) => {
                const head = option.group && option.group !== lastGroup ? option.group : null;
                lastGroup = option.group;
                return (
                  <div key={option.value || `leer-${index}`}>
                    {head && <div className="search-select-group">{head}</div>}
                    <div
                      role="option"
                      aria-selected={option.value === value}
                      data-active={index === active}
                      className={
                        'search-select-option' +
                        (index === active ? ' is-active' : '') +
                        (option.value === value ? ' is-selected' : '') +
                        (option.disabled ? ' is-disabled' : '')
                      }
                      // `mousedown` statt `click`: Sonst schlösse der Klick zuerst
                      // das Feld über den Außenklick-Wächter und der Eintrag
                      // bekäme das Ereignis nie.
                      onMouseDown={(event) => {
                        event.preventDefault();
                        choose(option);
                      }}
                      onMouseEnter={() => setActive(index)}
                    >
                      <span className="search-select-option-label">{option.label}</span>
                      {option.hint && <span className="search-select-option-hint">{option.hint}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="checkbox-row" style={disabled ? { opacity: 0.55 } : undefined}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

/* ------------------------------ Status ------------------------------- */

export function Loading({ label = 'Wird geladen …' }: { label?: string }) {
  return (
    <div className="loading">
      <div className="stack" style={{ alignItems: 'center', gap: 10 }}>
        <div className="spinner" />
        <span className="small subtle">{label}</span>
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
  icon,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon && <span className="empty-icon">{icon}</span>}
      <strong>{title}</strong>
      {hint && <div className="small">{hint}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

export function ErrorMessage({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  const details = (error as any)?.details as { path: string; message: string }[] | undefined;
  return (
    <div className="alert error">
      {message}
      {details?.length ? (
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {details.map((detail) => (
            <li key={detail.path}>
              {detail.path}: {detail.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ------------------------------ Avatar ------------------------------- */

export function Avatar({
  firstName,
  lastName,
  size = '',
  title,
}: {
  firstName?: string | null;
  lastName?: string | null;
  size?: '' | 'sm' | 'lg';
  title?: string;
}) {
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  return (
    <span className={`avatar ${size}`} style={{ background: avatarColor(name || '?') }} title={title ?? name}>
      {initials(firstName, lastName)}
    </span>
  );
}

/* ------------------------------- Tabs -------------------------------- */

/**
 * Abschnittsumschalter als Pillenleiste.
 *
 * Ersetzt die frühere Unterstrich-Variante: Die aktive Auswahl bleibt auch
 * dann eindeutig, wenn die Leiste auf einer farbigen Fläche liegt.
 */
export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { key: T; label: string; count?: number; icon?: ReactNode }[];
  active: T;
  onChange: (key: T) => void;
}) {
  return (
    <div className="pill-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={active === tab.key}
          className={`pill-tab ${active === tab.key ? 'active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.icon}
          {tab.label}
          {tab.count != null && <span className="count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

/* ----------------------------- Dropdown ------------------------------ */

/**
 * Alle offenen Menüs.
 *
 * Es soll immer nur eines offen sein. Der Außenklick allein genügt dafür
 * nicht: Er hängt an `mousedown` und bleibt bei Tastaturbedienung aus –
 * mit Enter oder Leertaste ließe sich sonst ein zweites Menü öffnen,
 * während das erste stehen bleibt.
 */
const openDropdowns = new Set<(open: boolean) => void>();

export function Dropdown({
  trigger,
  children,
  menuClassName = '',
}: {
  trigger: (open: boolean) => ReactNode;
  children: ReactNode;
  menuClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    openDropdowns.add(setOpen);
    const onMouseDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      openDropdowns.delete(setOpen);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = () =>
    setOpen((value) => {
      if (!value) for (const close of openDropdowns) close(false);
      return !value;
    });

  return (
    <div className="dropdown" ref={ref}>
      <div onClick={toggle}>{trigger(open)}</div>
      {open && (
        <div className={`dropdown-menu ${menuClassName}`} onClick={() => setOpen(false)}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Toasts ------------------------------- */

interface Toast {
  id: number;
  message: string;
  variant: 'info' | 'success' | 'error';
}

const ToastContext = createContext<{
  notify: (message: string, variant?: Toast['variant']) => void;
} | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const notify = useCallback((message: string, variant: Toast['variant'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, variant }]);
    setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4500);
  }, []);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-stack">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.variant}`}>
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast benötigt <ToastProvider>.');
  return context;
}

/* --------------------------- Datenabfrage ---------------------------- */

/** Minimalistischer Daten-Hook mit Ladezustand und manuellem Neuladen. */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
): { data: T | null; loading: boolean; error: unknown; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    loaderRef
      .current()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((caught) => {
        if (!cancelled) setError(caught);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, loading, error, reload: () => setNonce((value) => value + 1) };
}

/** Liest eine Bilddatei, skaliert sie herunter und kodiert sie als JPEG-Data-URL. */
export function readImageAsDataUrl(file: File, maxSize = 1280): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden.'));
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);
        const context = canvas.getContext('2d');
        if (!context) return resolve(String(reader.result));
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.78));
      };
      image.onerror = () => resolve(String(reader.result));
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}
